import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Textarea,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiChevronDown, FiCheck, FiSend, FiPaperclip, FiMic } from 'react-icons/fi';
import { Brain, Sparkles, Globe2 } from 'lucide-react';
import {
  AITextModelId,
  AITextModelOption,
  getDefaultTextModelId,
  getEnabledTextModels,
  findTextModel,
} from '../../utils/aiModels';
import { getAIUsageStatus, formatUsageText, AIUsageStatus } from '../../services/aiUsage';
import UploadPanel, { UploadPanelFile } from './UploadPanel';
import { processOcrAI, transcribeAudioAI } from '../../services/ai';
import { uploadFile as uploadArticleFile } from '../../services/articles';

export interface AIPromptInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string, modelId: AITextModelId) => void | Promise<unknown>;
  isSubmitting?: boolean;
  placeholder?: string;
  helperText?: string;
  initialModelId?: AITextModelId;
  onModelChange?: (id: AITextModelId) => void;
  onAttachClick?: () => void;
  onVoiceClick?: () => void;
}

const DAILY_LIMIT = 10;
const DAILY_LIMIT_REASONING = 5;
const DAILY_LIMIT_DEEPSEEK = Infinity; // Unlimited for DeepSeek

const MotionBox = motion(Box);

const getModelIcon = (model?: AITextModelOption, size = 14) => {
  if (!model) return null;
  const iconSize = size === 16 ? 20 : size; // Slightly larger for better visibility
  
  if (model.provider === 'deepseek') {
    return <img src="/deepseek.svg" alt="DeepSeek" width={iconSize} height={iconSize} />;
  }
  if (model.provider === 'mistral') {
    if (model.id === 'mistral-small-latest') {
      return <img src="/mistral_small.png" alt="Mistral" width={iconSize} height={iconSize} />;
    }
    return <img src="/ministral.png" alt="Ministral" width={iconSize} height={iconSize} />;
  }
  if (model.provider === 'openrouter') {
    return <Globe2 size={size} />; // Keep Lucide icon for OpenRouter
  }
  if (model.provider === 'grok') {
    return <img src="/grok.svg" alt="Grok" width={iconSize} height={iconSize} />;
  }
  return null;
};

const isReasoningModel = (modelId: AITextModelId): boolean => {
  return modelId.includes('reasoning') || modelId.includes('reasoner');
};

const AIPromptInput: React.FC<AIPromptInputProps> = ({
  value,
  onChange,
  onSubmit,
  isSubmitting,
  placeholder,
  helperText,
  initialModelId,
  onModelChange,
  onAttachClick,
  onVoiceClick,
}) => {
  const models = useMemo<AITextModelOption[]>(() => getEnabledTextModels(), []);
  const defaultId = useMemo<AITextModelId>(() => initialModelId || getDefaultTextModelId(), [initialModelId]);
  const [selectedModel, setSelectedModel] = useState<AITextModelId>(defaultId);
  const [usageStatus, setUsageStatus] = useState<AIUsageStatus>({});
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);

  // Define getRemainingToday before using it in useState
  const getRemainingToday = useCallback((modelId: AITextModelId): number => {
    const modelStatus = usageStatus[modelId];
    if (!modelStatus) return 10; // Default fallback
    return modelStatus.unlimited ? -1 : modelStatus.remaining;
  }, [usageStatus]);

  const [remaining, setRemaining] = useState<number>(() => getRemainingToday(defaultId));

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const minHeight = 96;
  const maxHeight = 260;

  // Clean white styling for AI input
  const bg = useColorModeValue('white', 'gray.800');
  const innerBg = useColorModeValue('gray.50', 'gray.700');
  const border = useColorModeValue('gray.200', 'gray.600');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');

  const toast = useToast();

  const {
    isOpen: isAttachOpen,
    onOpen: onAttachOpen,
    onClose: onAttachClose,
  } = useDisclosure();

  const {
    isOpen: isVoiceOpen,
    onOpen: onVoiceOpen,
    onClose: onVoiceClose,
  } = useDisclosure();

  const [ocrFiles, setOcrFiles] = useState<UploadPanelFile[]>([]);
  const [ocrText, setOcrText] = useState('');
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [waveform, setWaveform] = useState<number[]>(() => new Array(32).fill(0));

  const adjustHeight = useCallback((reset?: boolean) => {
    const el = textareaRef.current;
    if (!el) return;
    if (reset) {
      el.style.height = `${minHeight}px`;
      return;
    }
    el.style.height = 'auto';
    const next = Math.max(minHeight, Math.min(el.scrollHeight, maxHeight));
    el.style.height = `${next}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  useEffect(() => {
    const handleResize = () => adjustHeight();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [adjustHeight]);

  // Fetch usage status on mount and when model changes
  useEffect(() => {
    const fetchUsageStatus = async () => {
      setIsLoadingUsage(true);
      try {
        const status = await getAIUsageStatus();
        setUsageStatus(status);
      } catch (error) {
        console.error('Failed to fetch AI usage status:', error);
      } finally {
        setIsLoadingUsage(false);
      }
    };

    fetchUsageStatus();
  }, []);

  // Update remaining when usage status or selected model changes
  useEffect(() => {
    setRemaining(getRemainingToday(selectedModel));
  }, [getRemainingToday, selectedModel]);

  const getDailyLimit = (modelId: AITextModelId): number => {
    const modelStatus = usageStatus[modelId];
    if (!modelStatus) return 10; // Default fallback
    return modelStatus.limit;
  };

  const handleModelChange = (id: AITextModelId) => {
    setSelectedModel(id);
    if (onModelChange) onModelChange(id);
  };

  const handleSubmit = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    
    // Check remaining requests before submitting
    const modelStatus = usageStatus[selectedModel];
    if (modelStatus && !modelStatus.unlimited && modelStatus.remaining <= 0) {
      toast({
        title: 'Denní limit vyčerpán',
        description: 'Denní limit pro tento AI model byl vyčerpán. Zkuste to znovu zítra.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    try {
      const maybePromise = onSubmit(trimmed, selectedModel);
      if (maybePromise && typeof (maybePromise as any).then === 'function') {
        await (maybePromise as Promise<unknown>);
      }
      // Refresh usage status after successful submission
      const status = await getAIUsageStatus();
      setUsageStatus(status);
    } catch {
      // Do not decrement usage on error; parent is responsible for showing errors
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSubmitting) {
        handleSubmit();
      }
    }
  };

  const selected = findTextModel(selectedModel);

  const isDisabled = isSubmitting || !value.trim() || remaining <= 0;

  const handleInsertText = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const next = value ? `${value}\n\n${trimmed}` : trimmed;
    onChange(next);
  };

  const handleOcrUploadFinished = async (uploaded: UploadPanelFile[]) => {
    if (!uploaded || uploaded.length === 0) return;
    const last = uploaded[uploaded.length - 1];
    if (!last?.url) return;
    setIsOcrProcessing(true);
    try {
      const isImage = (last.type || '').startsWith('image/');
      const resp = await processOcrAI({
        document_url: isImage ? '' : last.url,
        image_url: isImage ? last.url : '',
        model: 'mistral-ocr-latest',
      });
      setOcrText(resp.text || '');
      if ((resp.text || '').trim()) {
        handleInsertText(resp.text || '');
        toast({
          title: 'Text z dokumentu byl vložen do AI promptu',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'OCR nenašlo čitelný text',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      toast({
        title: 'OCR selhalo',
        description: err?.message || 'Zkuste to prosím znovu.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsOcrProcessing(false);
    }
  };

  const stopMediaStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (recordingTimerRef.current !== null) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch {
        // ignore
      }
      audioContextRef.current = null;
      analyserRef.current = null;
    }
  };

  const startRecording = async () => {
    if (isRecording) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      toast({
        title: 'Prohlížeč nepodporuje nahrávání zvuku',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      audioChunksRef.current = [];
      setRecordingSeconds(0);

      try {
        const AudioCtx =
          typeof window !== 'undefined'
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ((window as any).AudioContext || (window as any).webkitAudioContext)
            : null;
        if (AudioCtx) {
          const ctx: AudioContext = audioContextRef.current || new AudioCtx();
          audioContextRef.current = ctx;
          const source = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          analyserRef.current = analyser;
          source.connect(analyser);

          const bufferLength = analyser.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);

          const tick = () => {
            if (!analyserRef.current) return;
            analyserRef.current.getByteTimeDomainData(dataArray);
            let sum = 0;
            for (let i = 0; i < bufferLength; i += 1) {
              const v = (dataArray[i] - 128) / 128;
              sum += v * v;
            }
            const rms = Math.sqrt(sum / bufferLength);
            const level = Math.max(0, Math.min(1, rms * 4));
            setWaveform((prev) =>
              prev.map((_, idx) => {
                const jitter = 0.2 * Math.sin(Date.now() / 120 + idx);
                const value = level + jitter;
                return Math.max(0.1, Math.min(1, value));
              }),
            );
            animationFrameRef.current = requestAnimationFrame(tick);
          };

          if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          animationFrameRef.current = requestAnimationFrame(tick);
        }
      } catch {
        // Vizualizace je best-effort, případné chyby ignorujeme
      }
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        stopMediaStream();
        setIsRecording(false);
        setHasRecording(audioChunksRef.current.length > 0);
      };

      recorder.start();
      setIsRecording(true);
      setHasRecording(false);
      if (recordingTimerRef.current !== null) {
        window.clearInterval(recordingTimerRef.current);
      }
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (err: any) {
      toast({
        title: 'Nelze získat přístup k mikrofonu',
        description: err?.message || 'Zkontrolujte oprávnění prohlížeče.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      stopMediaStream();
      setIsRecording(false);
    }
  };

  const handleTranscribe = async () => {
    if (!audioChunksRef.current.length) {
      toast({
        title: 'Nejprve nahrajte hlasovou zprávu',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    const file = new File([blob], 'voice-message.webm', { type: 'audio/webm' });
    setIsVoiceProcessing(true);
    try {
      const uploaded = await uploadArticleFile(file as any);
      const resp = await transcribeAudioAI({
        file_url: uploaded.url,
        model: 'voxtral-mini-latest',
        language: 'cs',
      });
      if ((resp.text || '').trim()) {
        handleInsertText(resp.text || '');
        toast({
          title: 'Přepis hlasu vložen do AI promptu',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onVoiceClose();
      } else {
        toast({
          title: 'Přepis neobsahuje text',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      toast({
        title: 'Přepis hlasu selhal',
        description: err?.message || 'Zkuste to prosím znovu.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsVoiceProcessing(false);
    }
  };

  return (
    <Box w="100%" py={2}>
      <Box
        bg={bg}
        borderRadius="2xl"
        p={3}
        borderWidth="1px"
        borderColor={border}
        boxShadow={useColorModeValue('sm', 'dark-lg')}
        _hover={{ borderColor: useColorModeValue('gray.300', 'gray.500') }}
        transition="all 0.2s ease"
      >
        <Box position="relative">
          <Flex direction="column">
            <Box overflowY="auto" maxH="360px">
              <Textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder || 'Napište, s čím má AI pomoci...'}
                resize="none"
                border="none"
                bg={innerBg}
                borderRadius="xl"
                px={4}
                py={3}
                minH={`${minHeight}px`}
                _focusVisible={{ boxShadow: 'none', bg: useColorModeValue('white', 'gray.700') }}
                _placeholder={{ color: placeholderColor }}
                fontSize="sm"
              />
            </Box>

            <Box
              h="60px"
              bg={innerBg}
              borderBottomRadius="xl"
              borderTop="1px solid"
              borderColor={border}
              mt={-1}
              _hover={{ bg: hoverBg }}
              transition="all 0.2s ease"
            >
              <Flex
                position="absolute"
                left={3}
                right={3}
                bottom={2}
                align="center"
                justify="space-between"
              >
                <HStack spacing={2} align="center">
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                      aria-label="Vybrat AI model"
                      px={2}
                      _hover={{ bg: hoverBg }}
                    >
                      <AnimatePresence mode="wait">
                        <MotionBox
                          key={selectedModel}
                          display="flex"
                          alignItems="center"
                          gap={2}
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 4 }}
                          transition={{ duration: 0.15 }}
                        >
                          <Box bg="blue.50" p={1} borderRadius="md">
                            {getModelIcon(selected, 14)}
                          </Box>
                          <Text fontSize="xs" fontWeight="600" color="gray.700">
                            {selected?.label || 'Model'}
                          </Text>
                          <FiChevronDown size={12} color="gray.500" />
                        </MotionBox>
                      </AnimatePresence>
                    </MenuButton>
                    <MenuList fontSize="sm" zIndex={1500}>
                      {models.map((model) => {
                        const rem = getRemainingToday(model.id as AITextModelId);
                        return (
                          <MenuItem
                            key={model.id}
                            onClick={() => handleModelChange(model.id)}
                          >
                            <HStack justify="space-between" w="100%" spacing={3}>
                              <Box>
                                <HStack spacing={2} align="center">
                                  <Box bg="gray.50" p={1} borderRadius="md">
                                    {getModelIcon(model, 16)}
                                  </Box>
                                  <Text fontWeight="600" color="gray.800">{model.label}</Text>
                                </HStack>
                                {model.description && (
                                  <Text fontSize="xs" color="gray.600" mt={1}>
                                    {model.description}
                                  </Text>
                                )}
                                {isReasoningModel(model.id) && (
                                  <Text fontSize="xs" color="blue.600" mt={1} fontWeight="medium">
                                    Reasoning model - přemýšlí před odpovědí
                                  </Text>
                                )}
                              </Box>
                              <HStack spacing={2} align="center">
                                {isLoadingUsage ? (
                                  <Spinner size="xs" color="gray.500" />
                                ) : (
                                  <Text fontSize="xs" color="gray.600" fontWeight="500">
                                    {model.provider === 'deepseek' ? '∞' : `${Math.max(0, rem)}/${getDailyLimit(model.id)}`}
                                  </Text>
                                )}
                                {selectedModel === model.id && (
                                  <Box bg="green.100" p={1} borderRadius="full">
                                    <FiCheck size={12} color="green.600" />
                                  </Box>
                                )}
                              </HStack>
                            </HStack>
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </Menu>

                  <Box h="4" w="1px" bg={border} mx={1} />

                  {/* Reasoning model warning */}
                  {isReasoningModel(selectedModel) && (
                    <Tooltip label="Reasoning modely přemýšlí nad odpovědí - poskytují hlubší analýzu, ale mohou trvat déle">
                      <Text fontSize="xs" color="blue.600" px={2} py={1} bg="blue.50" borderRadius="md" fontWeight="medium">
                        Reasoning
                      </Text>
                    </Tooltip>
                  )}

                  <Tooltip label="Přiložit soubor (obrázek / PDF)">
                    <IconButton
                      aria-label="Přiložit soubor"
                      icon={<FiPaperclip />}
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                      _hover={{ bg: hoverBg }}
                      onClick={() => {
                        if (onAttachClick) {
                          onAttachClick();
                        } else {
                          setOcrFiles([]);
                          setOcrText('');
                          onAttachOpen();
                        }
                      }}
                    />
                  </Tooltip>

                  <Tooltip label="Nahrát hlas pro přepis">
                    <IconButton
                      aria-label="Nahrát hlas"
                      icon={<FiMic />}
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                      _hover={{ bg: hoverBg }}
                      onClick={() => {
                        if (onVoiceClick) {
                          onVoiceClick();
                        } else {
                          audioChunksRef.current = [];
                          setHasRecording(false);
                          setIsVoiceProcessing(false);
                          onVoiceOpen();
                        }
                      }}
                    />
                  </Tooltip>

                  {helperText && (
                    <Text fontSize="xs" color="gray.500" ml={2} noOfLines={1}>
                      {helperText}
                    </Text>
                  )}
                </HStack>

                <HStack spacing={3} align="center">
                  {isLoadingUsage ? (
                    <Spinner size="xs" color="gray.500" />
                  ) : (
                    <Text fontSize="xs" color="gray.600" fontWeight="500">
                      {selected?.provider === 'deepseek' ? '∞' : `Zbývá ${getDailyLimit(selectedModel) === Infinity ? '∞' : `${remaining}/${getDailyLimit(selectedModel)}`}`}
                    </Text>
                  )}
                  <Tooltip
                    label={
                      remaining <= 0
                        ? 'Denní limit pro tento model byl vyčerpán.'
                        : 'Odeslat (Enter). Shift+Enter vloží nový řádek.'
                    }
                  >
                    <span>
                      <IconButton
                        aria-label="Odeslat AI požadavek"
                        icon={<FiSend />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        _hover={{ bg: 'blue.50' }}
                        isLoading={isSubmitting && !isLoadingUsage}
                        isDisabled={isDisabled}
                        onClick={handleSubmit}
                      />
                    </span>
                  </Tooltip>
                </HStack>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Box>

      <Modal isOpen={isAttachOpen} onClose={onAttachClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Přiložit soubor pro OCR</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UploadPanel
              label="Soubor pro AI (PDF / obrázek)"
              description="Nahrajte dokument nebo obrázek, ze kterého má AI přečíst text."
              value={ocrFiles}
              onChange={setOcrFiles}
              accept="application/pdf,image/*"
              maxFiles={1}
              onUploadFinished={handleOcrUploadFinished}
            />
            {isOcrProcessing && (
              <HStack mt={4} spacing={2} align="center">
                <Spinner size="sm" />
                <Text fontSize="sm">Probíhá zpracování textu…</Text>
              </HStack>
            )}
            {!!ocrText && !isOcrProcessing && (
              <Box mt={4}>
                <Text fontSize="sm" fontWeight="semibold" mb={2}>
                  Rozpoznaný text
                </Text>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={border}
                  bg={innerBg}
                  p={3}
                  maxH="200px"
                  overflowY="auto"
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                >
                  {ocrText}
                </Box>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onAttachClose}>
                Zavřít
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isVoiceOpen} onClose={() => {
        if (isRecording) {
          stopRecording();
        }
        onVoiceClose();
      }} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Hlasový vstup pro AI</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              Nahrajte krátkou hlasovou zprávu, kterou AI přepíše do textu a vloží do promptu.
            </Text>
            <HStack spacing={4} align="center">
              <Button
                onClick={isRecording ? stopRecording : startRecording}
                isDisabled={isVoiceProcessing}
                bg={isRecording ? 'red.500' : 'blackAlpha.700'}
                color="white"
                _hover={{
                  bg: isRecording ? 'red.600' : 'blackAlpha.800',
                }}
                _active={{
                  bg: isRecording ? 'red.700' : 'blackAlpha.900',
                }}
              >
                {isRecording ? 'Zastavit nahrávání' : 'Začít nahrávat'}
              </Button>
              {hasRecording && !isRecording && (
                <Text fontSize="sm" color="green.500">
                  Nahrávka je připravena k přepisu.
                </Text>
              )}
            </HStack>
            {isRecording && (
              <Box mt={4}>
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="xs" color="gray.500">
                    Nahrávání…
                  </Text>
                  <Text fontSize="xs" fontFamily="mono" color="gray.600">
                    {`${String(Math.floor(recordingSeconds / 60)).padStart(2, '0')}:${String(
                      recordingSeconds % 60,
                    ).padStart(2, '0')}`}
                  </Text>
                </HStack>
                <HStack spacing={1} align="flex-end" h="40px">
                  {waveform.map((value, idx) => (
                    <Box
                      key={idx}
                      w="2px"
                      borderRadius="full"
                      bg="green.400"
                      h={`${Math.max(8, value * 100)}%`}
                    />
                  ))}
                </HStack>
              </Box>
            )}
            {isVoiceProcessing && (
              <HStack mt={4} spacing={2} align="center">
                <Spinner size="sm" />
                <Text fontSize="sm">Probíhá přepis hlasu…</Text>
              </HStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                onClick={handleTranscribe}
                isDisabled={!hasRecording || isRecording || isVoiceProcessing}
                isLoading={isVoiceProcessing}
                bg="blackAlpha.800"
                color="white"
                _hover={{ bg: 'blackAlpha.900' }}
                _active={{ bg: 'blackAlpha.900' }}
              >
                Přepsat a vložit do promptu
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  if (isRecording) {
                    stopRecording();
                  }
                  onVoiceClose();
                }}
              >
                Zavřít
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AIPromptInput;
